import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ScannerData } from '../../types/ScannerData';
import type { OptionRow } from '../../services/gexCalculator';
import type { MarketAnalyticsSnapshot } from '../../types/marketAnalytics';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiUrl } from '../../config/api';
import { buildMarketAnalyticsSnapshot } from '../../utils/marketRegimeEngine';
import { regimeMeta, RISK_LEVEL_COLORS } from '../../utils/regimeTheme';
import { runMonteCarloGBM } from '../../utils/firstGlanceBsAndMonteCarlo';
import { computeChainAverageTargets } from '../../utils/firstGlanceRangeModels';
import { normalizeScannerExpiryToIso } from '../../utils/firstGlanceSpotGammaInsight';
import MarketAnalyticsGlance from '../analytics/MarketAnalyticsGlance';

// ── Helpers ────────────────────────────────────────────────────────────────
function fmt(n: number | null | undefined, d = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtPrice(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function safeNum(v: unknown, fb = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

function getSpot(rows: ScannerData[]): number {
  for (const r of rows) {
    const p = safeNum(r['Underlying Price'] ?? r.underlying_price);
    if (p > 0) return p;
  }
  return 0;
}

function getFirstExpiry(rows: ScannerData[]): string {
  for (const r of rows) {
    const raw = String(r['Expiry Date'] || '').trim();
    if (raw) return normalizeScannerExpiryToIso(raw) || raw;
  }
  return '';
}

function getAtmIv(rows: ScannerData[], spot: number): number {
  if (spot <= 0 || !rows.length) return 0;
  let best = 0;
  let bestDist = Infinity;
  for (const r of rows) {
    const iv = safeNum(r['Implied Volatility'] ?? r.iv);
    const strike = safeNum(r.Strike);
    if (iv <= 0 || !Number.isFinite(strike)) continue;
    const dist = Math.abs(strike - spot);
    if (dist < bestDist) { bestDist = dist; best = iv; }
  }
  return best;
}

function getAtmSnapshot(rows: ScannerData[], spot: number): { call: ScannerData | null; put: ScannerData | null } {
  if (spot <= 0 || !rows.length) return { call: null, put: null };
  let bestDist = Infinity;
  let bestStrike = 0;
  const strikes: number[] = [];
  const seen = new Set<number>();
  for (const r of rows) {
    const s = safeNum(r.Strike);
    if (!seen.has(s)) { seen.add(s); strikes.push(s); }
  }
  for (const s of strikes) {
    const d = Math.abs(s - spot);
    if (d < bestDist) { bestDist = d; bestStrike = s; }
  }
  let call: ScannerData | null = null;
  let put: ScannerData | null = null;
  for (const r of rows) {
    if (safeNum(r.Strike) !== bestStrike) continue;
    const ot = (r['Option Type'] ?? r.option_type ?? '').toUpperCase();
    if (ot === 'C' && !call) call = r;
    else if (ot === 'P' && !put) put = r;
  }
  return { call, put };
}

// ── Credit Hunter types ────────────────────────────────────────────────────
type CreditData = {
  bestVertical: { short: number | null; long: number | null; expiry: string | null; rationale: string } | null;
  top3: { strike: number; score: number }[];
  context: string | null;
  days: { date: string; topStrikes: { strike: number; strengthScore: number; whaleConfirmed: boolean; deltaFlowTotal: number }[] }[];
  loading: boolean;
};

// ── Component ──────────────────────────────────────────────────────────────
export interface MarketAnalysisPageProps {
  scannerRows?: ScannerData[];
  underlying?: string;
}

const MarketAnalysisPage: React.FC<MarketAnalysisPageProps> = ({ scannerRows = [], underlying = 'SPX' }) => {
  const { language } = useLanguage();
  const ar = language === 'ar';

  // ── Core data ──────────────────────────────────────────────────────────
  const spot = useMemo(() => getSpot(scannerRows), [scannerRows]);
  const expiryIso = useMemo(() => getFirstExpiry(scannerRows), [scannerRows]);
  const atmIv = useMemo(() => getAtmIv(scannerRows, spot), [scannerRows, spot]);

  const snapshot: MarketAnalyticsSnapshot | null = useMemo(() => {
    if (!scannerRows.length || spot <= 0) return null;
    try {
      return buildMarketAnalyticsSnapshot(underlying.toUpperCase(), scannerRows as OptionRow[], null);
    } catch { return null; }
  }, [scannerRows, underlying, spot]);

  // ── Monte Carlo ────────────────────────────────────────────────────────
  const mcResult = useMemo(() => {
    if (spot <= 0 || atmIv <= 0) return null;
    const sigma = atmIv >= 1 ? atmIv / 100 : atmIv;
    const T = 1 / 252; // 1 session
    const rangeLow = spot * 0.95;
    const rangeHigh = spot * 1.05;
    return runMonteCarloGBM({ S0: spot, T, sigma, nPaths: 20000, seed: 42, rangeLow, rangeHigh });
  }, [spot, atmIv]);

  // ── Chain averages ─────────────────────────────────────────────────────
  const chainAvg = useMemo(() => {
    if (!scannerRows.length || !expiryIso) return null;
    return computeChainAverageTargets(scannerRows, expiryIso, underlying.toUpperCase());
  }, [scannerRows, expiryIso, underlying]);

  // ── Greeks narrative (simplified inline) ────────────────────────────────
  const atmSnap = useMemo(() => getAtmSnapshot(scannerRows, spot), [scannerRows, spot]);
  const atmGamma = useMemo(() => {
    const g = safeNum(atmSnap.call?.Gamma ?? atmSnap.call?.gamma ?? atmSnap.put?.Gamma ?? atmSnap.put?.gamma);
    return g > 0 ? g : null;
  }, [atmSnap]);
  const atmDelta = useMemo(() => {
    const d = safeNum(atmSnap.call?.Delta ?? atmSnap.call?.delta);
    return Number.isFinite(d) ? d : null;
  }, [atmSnap]);

  // ── Credit Hunter ──────────────────────────────────────────────────────
  const [creditData, setCreditData] = useState<CreditData>({
    bestVertical: null, top3: [], context: null, days: [], loading: false,
  });
  const [minOi, setMinOi] = useState(() => {
    try { const v = localStorage.getItem('mmec-credit-min-oi'); return v ? Number(v) : 100; } catch { return 100; }
  });
  const [dteTarget, setDteTarget] = useState(() => {
    try { const v = localStorage.getItem('mmec-credit-dte'); return v ? Number(v) : 7; } catch { return 7; }
  });

  const fetchCreditHunter = useCallback(async () => {
    setCreditData((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch(getApiUrl('/api/smart-flow/gamma-credit-hunter/scan'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ underlying, min_oi: minOi, dte_target: dteTarget, min_gamma: 0 }),
      });
      const json = await res.json();
      if (json.success) {
        setCreditData({
          bestVertical: json.best_credit_vertical ? {
            short: json.best_credit_vertical.short_strike,
            long: json.best_credit_vertical.long_strike,
            expiry: json.best_credit_vertical.expiry,
            rationale: json.best_credit_vertical.rationale || '',
          } : null,
          top3: (json.weekly_top_3 || []).map((w: { strike: number; weekly_strength: number }) => ({
            strike: w.strike, score: w.weekly_strength,
          })),
          context: json.context_line || null,
          days: (json.days || []).map((d: { date: string; top_strikes?: { strike: number; strength_score: number; whale_confirmed: boolean; delta_flow_total: number }[] }) => ({
            date: d.date,
            topStrikes: (d.top_strikes || []).slice(0, 5).map((t) => ({
              strike: t.strike,
              strengthScore: t.strength_score,
              whaleConfirmed: t.whale_confirmed,
              deltaFlowTotal: t.delta_flow_total,
            })),
          })),
          loading: false,
        });
      } else {
        setCreditData((prev) => ({ ...prev, loading: false }));
      }
    } catch {
      setCreditData((prev) => ({ ...prev, loading: false }));
    }
  }, [underlying, minOi, dteTarget]);

  // Auto-fetch on mount
  useEffect(() => { fetchCreditHunter(); }, [fetchCreditHunter]);

  // ── Derived values ─────────────────────────────────────────────────────
  const regime = snapshot ? regimeMeta(snapshot.regime) : null;
  const gex = snapshot?.gex;
  const pin = snapshot?.pin_analytics;
  const volFlow = snapshot?.vol_flow;
  const rnd = snapshot?.rnd;
  const tier2 = snapshot?.tier2;

  const gammaFlipDist = gex?.gamma_flip_distance;
  const gammaFlipLevel = gex?.zero_gamma ?? null;

  // ── Styling ────────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    backgroundColor: 'var(--elev)',
    border: '1px solid var(--hair)',
    borderRadius: 10,
    padding: '14px 16px',
  };
  const sectionLabel: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted-2)', marginBottom: 10,
  };
  const statRow: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', fontSize: 12.5, fontVariantNumeric: 'tabular-nums',
  };
  /** Small value tiles inside a card. */
  const chipBg = 'var(--elev2)';
  const tileValue: React.CSSProperties = { fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' };
  const tileLabel: React.CSSProperties = { fontSize: 10, color: 'var(--muted)' };

  return (
    <div style={{ backgroundColor: 'var(--base)', color: 'var(--txt)', fontFamily: 'var(--font-ui)', padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* HEADER                                                             */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>{underlying.toUpperCase()}</div>
        {spot > 0 && (
          <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--txt-2)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{fmtPrice(spot)}</div>
        )}
        {regime && snapshot && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 12px', borderRadius: 999,
            backgroundColor: regime.accentSoft, border: `1px solid ${regime.accent}`,
          }}>
            <span style={{ fontSize: 16 }}>{regime.emoji}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: regime.accent }}>
              {ar ? regime.labelAr : regime.labelEn}
            </span>
            <span style={{ fontSize: 11, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
              {Math.round(snapshot.regime_confidence * 100)}%
            </span>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 3-COLUMN GRID                                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>

        {/* ── 1. Regime & Risk ─────────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'النظام والمخاطر' : 'Regime & Risk'}</div>
          {snapshot ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 28 }}>{regime?.emoji}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: regime?.accent }}>
                    {ar ? regime?.labelAr : regime?.labelEn}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {ar ? 'مخاطر' : 'Risk'}{' '}
                    <span style={{ color: RISK_LEVEL_COLORS[snapshot.risk_level], fontWeight: 600 }}>
                      {snapshot.risk_level}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: chipBg, fontSize: 11 }}>
                  {ar ? 'ثقة' : 'Conf'} {Math.round(snapshot.regime_confidence * 100)}%
                </span>
                {snapshot.transitioned && (
                  <span style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: 'var(--warn-dim)', color: 'var(--warn)', fontSize: 11 }}>
                    {ar ? 'تحول' : 'Transitioned'}
                  </span>
                )}
                {gex?.trap_zone && (
                  <span style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: 'var(--down-dim)', color: 'var(--down)', fontSize: 11, fontWeight: 600 }}>
                    {ar ? '⚠ فخ جاما' : '⚠ Gamma Trap'}
                  </span>
                )}
              </div>
              {tier2 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 4 }}>
                  <div style={{ padding: '4px 8px', borderRadius: 6, backgroundColor: chipBg, fontSize: 10 }}>
                    <div style={tileLabel}>{ar ? 'Dealer' : 'Dealer'}</div>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{tier2.dealer_constraint ?? '—'}</div>
                  </div>
                  <div style={{ padding: '4px 8px', borderRadius: 6, backgroundColor: chipBg, fontSize: 10 }}>
                    <div style={tileLabel}>{ar ? 'CWR' : 'CWR'}</div>
                    <div style={{ ...tileValue, fontSize: 12 }}>{tier2.cwr_proxy != null ? `${tier2.cwr_proxy.toFixed(1)}%` : '—'}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ar ? 'تحتاج بيانات سلسلة خيارات' : 'Needs options chain data'}</div>
          )}
        </div>

        {/* ── 2. Sentiment & Direction ─────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'المشاعر والاتجاه' : 'Sentiment & Direction'}</div>
          {volFlow ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <div style={{ padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                  <div style={tileLabel}>{ar ? 'PCR مشتري' : 'Buyer PCR'}</div>
                  <div style={tileValue}>
                    {volFlow.signed_buyer_pcr != null ? volFlow.signed_buyer_pcr.toFixed(3) : '—'}
                  </div>
                </div>
                <div style={{ padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                  <div style={tileLabel}>VPIN</div>
                  <div style={tileValue}>
                    {volFlow.vpin_rolling != null ? volFlow.vpin_rolling.toFixed(3) : '—'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {volFlow.flow_toxicity && (
                  <span style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: 'var(--down-dim)', color: 'var(--down)', fontSize: 11, fontWeight: 600 }}>
                    {ar ? '⚠ سمية التدفق' : '⚠ Flow Toxicity'}
                  </span>
                )}
                {rnd && (
                  <span style={{
                    padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    backgroundColor: rnd.regime_skew === 'bullish' ? 'var(--up-dim)' : rnd.regime_skew === 'bearish' ? 'var(--down-dim)' : chipBg,
                    color: rnd.regime_skew === 'bullish' ? 'var(--up)' : rnd.regime_skew === 'bearish' ? 'var(--down)' : 'var(--muted)',
                  }}>
                    {ar ? 'انحياز' : 'Skew'}: {rnd.regime_skew}
                  </span>
                )}
              </div>
              {volFlow.iv_har_gap != null && (
                <div style={statRow}>
                  <span style={{ color: 'var(--muted)' }}>{ar ? 'IV - HAR' : 'IV-HAR Gap'}</span>
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', color: volFlow.iv_har_gap > 0 ? 'var(--down)' : 'var(--up)' }}>
                    {volFlow.iv_har_gap > 0 ? '+' : ''}{(volFlow.iv_har_gap * 100).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ar ? 'تحتاج بيانات' : 'Needs data'}</div>
          )}
        </div>

        {/* ── 3. Range & Volatility ────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'النطاق والتقلب' : 'Range & Volatility'}</div>
          {snapshot?.vol_analytics ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <div style={{ padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                  <div style={tileLabel}>ATM IV</div>
                  <div style={tileValue}>
                    {snapshot.vol_analytics.atm_iv != null ? `${(snapshot.vol_analytics.atm_iv * 100).toFixed(1)}%` : '—'}
                  </div>
                </div>
                <div style={{ padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                  <div style={tileLabel}>{ar ? 'VRP' : 'VRP'}</div>
                  <div style={tileValue}>
                    {snapshot.vol_analytics.short_vrp != null ? `${(snapshot.vol_analytics.short_vrp * 100).toFixed(1)}%` : '—'}
                  </div>
                </div>
              </div>
              {spot > 0 && atmIv > 0 && (() => {
                const sigma = atmIv >= 1 ? atmIv / 100 : atmIv;
                const move1d = spot * sigma * Math.sqrt(1 / 252);
                const move1w = spot * sigma * Math.sqrt(5 / 252);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <div style={{ padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                      <div style={tileLabel}>{ar ? 'تحرك 1 يوم' : '1D Move'}</div>
                      <div style={{ ...tileValue, fontSize: 13 }}>${fmt(move1d, 0)} ({((move1d / spot) * 100).toFixed(2)}%)</div>
                    </div>
                    <div style={{ padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                      <div style={tileLabel}>{ar ? 'تحرك أسبوع' : '1W Move'}</div>
                      <div style={{ ...tileValue, fontSize: 13 }}>${fmt(move1w, 0)} ({((move1w / spot) * 100).toFixed(2)}%)</div>
                    </div>
                  </div>
                );
              })()}
              {snapshot.vol_analytics.iv_har_forecast != null && (
                <div style={statRow}>
                  <span style={{ color: 'var(--muted)' }}>{ar ? 'تنبؤ HAR' : 'HAR Forecast'}</span>
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{(snapshot.vol_analytics.iv_har_forecast * 100).toFixed(1)}%</span>
                </div>
              )}
              <div style={statRow}>
                <span style={{ color: 'var(--muted)' }}>{ar ? 'وضع الجلسة' : 'Session'}</span>
                <span style={{ fontWeight: 600 }}>{snapshot.vol_analytics.intraday_regime}</span>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ar ? 'تحتاج بيانات' : 'Needs data'}</div>
          )}
        </div>

        {/* ── 4. Gamma Flip Proximity ──────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'قرب انعكاس جاما' : 'Gamma Flip Proximity'}</div>
          {gex ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gammaFlipLevel != null && (
                <div style={{ padding: '8px 12px', borderRadius: 8, backgroundColor: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)' }}>
                  <div style={tileLabel}>{ar ? 'مستوى الانعكاس' : 'Flip Level'}</div>
                  <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: '#8B5CF6' }}>{fmtPrice(gammaFlipLevel)}</div>
                </div>
              )}
              {gammaFlipDist != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    flex: 1, height: 6, borderRadius: 3,
                    backgroundColor: 'var(--elev2)',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 3,
                      width: `${Math.min(100, gammaFlipDist * 100 * 5)}%`,
                      backgroundColor: gammaFlipDist < 0.02 ? 'var(--down)' : gammaFlipDist < 0.05 ? 'var(--warn)' : 'var(--up)',
                    }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: gammaFlipDist < 0.02 ? 'var(--down)' : gammaFlipDist < 0.05 ? 'var(--warn)' : 'var(--up)' }}>
                    {(gammaFlipDist * 100).toFixed(1)}%
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {gex.call_wall != null && (
                  <span style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: 'var(--up-dim)', color: 'var(--up)', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                    {ar ? 'جدار كول' : 'Call Wall'}: {fmtPrice(gex.call_wall)}
                  </span>
                )}
                {gex.put_wall != null && (
                  <span style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: 'var(--down-dim)', color: 'var(--down)', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                    {ar ? 'جدار بوت' : 'Put Wall'}: {fmtPrice(gex.put_wall)}
                  </span>
                )}
              </div>
              <div style={statRow}>
                <span style={{ color: 'var(--muted)' }}>{ar ? 'GEX صافي' : 'Net GEX'}</span>
                <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', color: gex.net_gex >= 0 ? 'var(--up)' : 'var(--down)' }}>
                  {gex.net_gex >= 0 ? '+' : ''}{(gex.net_gex / 1e6).toFixed(1)}M
                </span>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ar ? 'تحتاج بيانات' : 'Needs data'}</div>
          )}
        </div>

        {/* ── 5. Pin & Gravity ─────────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'التثبيت والجاذبية' : 'Pin & Gravity'}</div>
          {pin || gex ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gex?.pin_probability != null && (
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--warn)' }}>
                    {Math.round(gex.pin_probability * 100)}%
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{ar ? 'احتمال التثبيت' : 'Pin Probability'}</div>
                </div>
              )}
              {pin?.gravity_strike != null && (
                <div style={{ padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                  <div style={tileLabel}>{ar ? ' strike الجاذبية' : 'Gravity Strike'}</div>
                  <div style={{ ...tileValue, color: '#8B5CF6' }}>{fmtPrice(pin.gravity_strike)}</div>
                  {pin.gravity_score != null && (
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{ar ? 'النتيجة' : 'Score'}: {pin.gravity_score.toFixed(1)}</div>
                  )}
                </div>
              )}
              {pin?.pin_defense_active && (
                <span style={{ padding: '3px 8px', borderRadius: 6, backgroundColor: 'var(--acc-dim)', color: 'var(--acc)', fontSize: 11, fontWeight: 600, alignSelf: 'flex-start' }}>
                  {ar ? '🛡 دفاع نشط' : '🛡 Pin Defense Active'}
                </span>
              )}
              {pin?.volume_clusters && pin.volume_clusters.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--muted-2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                    {ar ? 'مراكز الحجم' : 'Volume Clusters'}
                  </div>
                  {pin.volume_clusters.slice(0, 4).map((cl, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11.5, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid var(--hair)' }}>
                      <span>{fmtPrice(cl.strike)}</span>
                      <span style={{ color: 'var(--muted)' }}>{(cl.volume / 1000).toFixed(0)}k</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ar ? 'تحتاج بيانات' : 'Needs data'}</div>
          )}
        </div>

        {/* ── 6. Greeks Narrative ──────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'سرد الجريكس' : 'Greeks Narrative'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--txt-2)', marginBottom: 3 }}>
                {ar ? 'التقلب الضمني' : 'Implied Volatility'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
                {atmIv > 0
                  ? (ar ? 'التقلب الضمني يحدد عرض نطاقات الحركة (√الوقت). IV أعلى → نطاقات أوسع.' : 'Implied volatility scales the width of move bands (√time). Higher IV → wider ranges.')
                  : (ar ? 'حمّل IV الضمني لتثبيت العرض.' : 'Load ATM implied volatility to anchor width.')}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--txt-2)', marginBottom: 3 }}>
                {ar ? 'جاما' : 'Gamma'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
                {ar
                  ? 'جاما أعلى قرب الـ ATM: يقيس سرعة تغير Δ لكل $1 في السعر.'
                  : 'Gamma is highest near ATM: it measures how fast Δ moves per $1 in spot — local curvature.'}
                {atmGamma != null && (
                  <span style={{ color: 'var(--txt)' }}> γ ≈ {atmGamma.toExponential(2)}</span>
                )}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--txt-2)', marginBottom: 3 }}>
                {ar ? '_DELTA' : 'Delta'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
                {ar
                  ? '|Δ| قريب من 0.5 يعني أن السلسلة مركّزة على الـ ATM.'
                  : '|Δ| near 0.5 means the chain is centered on ATM.'}
                {atmDelta != null && (
                  <span style={{ color: 'var(--txt)' }}> Δ ≈ {atmDelta.toFixed(3)}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 2-COLUMN ROW                                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 16 }}>

        {/* ── 7. Chain Targets ─────────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'أهداف السلسلة' : 'Chain Targets'}</div>
          {chainAvg && chainAvg.rowCount > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {chainAvg.oiWeightedStrike != null && (
                <div style={statRow}>
                  <span style={{ color: 'var(--muted)' }}>{ar ? 'موزون OI' : 'OI-Weighted'}</span>
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', color: '#8B5CF6' }}>{fmtPrice(chainAvg.oiWeightedStrike)}</span>
                </div>
              )}
              {chainAvg.volumeWeightedStrike != null && (
                <div style={statRow}>
                  <span style={{ color: 'var(--muted)' }}>{ar ? 'موزون الحجم' : 'Volume-Weighted'}</span>
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--acc)' }}>{fmtPrice(chainAvg.volumeWeightedStrike)}</span>
                </div>
              )}
              {chainAvg.combinedAverageStrike != null && (
                <div style={{ ...statRow, borderTop: '1px solid var(--hair)', paddingTop: 8, marginTop: 4 }}>
                  <span style={{ color: 'var(--txt-2)', fontWeight: 600 }}>{ar ? 'المتوسط المشترك' : 'Combined Avg'}</span>
                  <span style={{ fontWeight: 600, fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--warn)' }}>{fmtPrice(chainAvg.combinedAverageStrike)}</span>
                </div>
              )}
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                {chainAvg.rowCount} {ar ? 'صف' : 'rows'} · {(chainAvg.totalOi / 1000).toFixed(0)}k OI · {(chainAvg.totalVol / 1000).toFixed(0)}k Vol
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ar ? 'تحتاج بيانات' : 'Needs data'}</div>
          )}
        </div>

        {/* ── 8. Monte Carlo ───────────────────────────────────────────── */}
        <div style={card}>
          <div style={sectionLabel}>{ar ? 'مونت كارلو' : 'Monte Carlo Simulation'}</div>
          {mcResult ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                {[
                  { label: 'P05', value: mcResult.p05 },
                  { label: 'P25', value: mcResult.p25 },
                  { label: 'P50', value: mcResult.p50 },
                  { label: 'P75', value: mcResult.p75 },
                  { label: 'P95', value: mcResult.p95 },
                  { label: ar ? 'المتوسط' : 'Mean', value: mcResult.mean },
                ].map(({ label, value }) => (
                  <div key={label} style={{ padding: '4px 8px', borderRadius: 6, backgroundColor: chipBg, fontSize: 10 }}>
                    <div style={tileLabel}>{label}</div>
                    <div style={{ ...tileValue, fontSize: 12 }}>${fmt(value, 0)}</div>
                  </div>
                ))}
              </div>
              <div style={statRow}>
                <span style={{ color: 'var(--muted)' }}>{ar ? 'داخل النطاق' : 'In Band'}</span>
                <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--up)' }}>{(mcResult.fracInDisplayedBand * 100).toFixed(1)}%</span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                {mcResult.paths.toLocaleString()} {ar ? 'مسار' : 'paths'} · σ = {(mcResult.sigma * 100).toFixed(1)}%
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ar ? 'تحتاج IV وسعر' : 'Needs IV and spot price'}</div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* BL DISTRIBUTION (full width)                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ marginTop: 16 }}>
        <MarketAnalyticsGlance
          snapshot={snapshot}
          showRegime={false}
          showGexStrip={false}
          showBlPredictors
          showBlChart
          chartHeight={220}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* CREDIT HUNTER SECTION                                            */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={sectionLabel}>{ar ? 'الصيد الائتماني' : 'Credit Hunter'}</div>
          <button
            onClick={fetchCreditHunter}
            disabled={creditData.loading}
            style={{
              height: 28, padding: '0 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
              backgroundColor: creditData.loading ? 'var(--elev2)' : 'var(--acc)',
              color: creditData.loading ? 'var(--muted)' : 'var(--void)',
              fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-ui)',
            }}
          >
            {creditData.loading ? '...' : ar ? 'مسح' : 'Scan'}
          </button>
        </div>
        {/* Config controls */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted)' }}>
            {ar ? 'الحد الأدنى OI' : 'Min OI'}
            <input
              type="number"
              min={0}
              step={50}
              value={minOi}
              onChange={(e) => {
                const v = Math.max(0, Number(e.target.value) || 0);
                setMinOi(v);
                try { localStorage.setItem('mmec-credit-min-oi', String(v)); } catch {}
              }}
              style={{
                width: 64, padding: '3px 6px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-mono)',
                backgroundColor: 'var(--base)', color: 'var(--txt)',
                border: '1px solid var(--hair)', borderRadius: 6, textAlign: 'center',
              }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--muted)' }}>
            {ar ? 'DTE' : 'DTE'}
            <input
              type="number"
              min={1}
              max={45}
              step={1}
              value={dteTarget}
              onChange={(e) => {
                const v = Math.max(1, Math.min(45, Number(e.target.value) || 7));
                setDteTarget(v);
                try { localStorage.setItem('mmec-credit-dte', String(v)); } catch {}
              }}
              style={{
                width: 48, padding: '3px 6px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-mono)',
                backgroundColor: 'var(--base)', color: 'var(--txt)',
                border: '1px solid var(--hair)', borderRadius: 6, textAlign: 'center',
              }}
            />
          </label>
          <span style={{ fontSize: 10, color: 'var(--muted)' }}>
            {ar ? '_thresholds منخفضة لبيانات DTE أقصر' : 'Lower thresholds for shorter DTE data'}
          </span>
        </div>

        {creditData.bestVertical ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Best credit vertical */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted-2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                {ar ? 'أفضل انتشار عمودي' : 'Best Credit Vertical'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, padding: '8px 10px', borderRadius: 6, backgroundColor: 'var(--down-dim)' }}>
                  <div style={tileLabel}>{ar ? 'بيع' : 'Short'}</div>
                  <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--down)' }}>{creditData.bestVertical.short != null ? fmtPrice(creditData.bestVertical.short) : '—'}</div>
                </div>
                <div style={{ flex: 1, padding: '8px 10px', borderRadius: 6, backgroundColor: 'var(--up-dim)' }}>
                  <div style={tileLabel}>{ar ? 'شراء' : 'Long'}</div>
                  <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--up)' }}>{creditData.bestVertical.long != null ? fmtPrice(creditData.bestVertical.long) : '—'}</div>
                </div>
              </div>
              {creditData.bestVertical.expiry && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  {ar ? 'انتهاء' : 'Expiry'}: <span style={{ color: 'var(--txt)', fontWeight: 600 }}>{creditData.bestVertical.expiry}</span>
                </div>
              )}
              {creditData.bestVertical.rationale && (
                <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, marginTop: 4 }}>{creditData.bestVertical.rationale}</div>
              )}
            </div>

            {/* Weekly top 3 */}
            {creditData.top3.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted-2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                  {ar ? 'أعلى 3 ضربات أسبوعية' : 'Weekly Top 3 Strikes'}
                </div>
                {creditData.top3.map((t, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid var(--hair)' }}>
                    <span style={{ color: 'var(--txt)', fontFamily: 'var(--font-mono)' }}>{fmtPrice(t.strike)}</span>
                    <span style={{ color: 'var(--warn)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{ar ? 'النتيجة' : 'Score'} {t.score}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Per-day top strikes with whale detection */}
            {creditData.days.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted-2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  {ar ? 'أقوى الضربات حسب اليوم' : 'Top Strikes by Day'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                  {creditData.days.map((day) => (
                    <div key={day.date} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--hair)', backgroundColor: 'var(--elev2)' }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--txt-2)', marginBottom: 4, fontFamily: 'var(--font-mono)' }}>{day.date}</div>
                      {day.topStrikes.map((t, j) => (
                        <div key={j} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0', fontSize: 11.5, fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                          <span>{fmtPrice(t.strike)}</span>
                          <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {t.whaleConfirmed && (
                              <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 3, backgroundColor: 'rgba(139,92,246,0.15)', color: '#8B5CF6', fontWeight: 700 }}>W</span>
                            )}
                            <span style={{ color: 'var(--warn)', fontWeight: 600, fontSize: 11 }}>{t.strengthScore}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {creditData.context && (
              <div style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.4, padding: '6px 8px', borderRadius: 6, backgroundColor: chipBg }}>
                {creditData.context}
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            {creditData.loading ? (ar ? 'جاري المسح...' : 'Scanning...') : (ar ? 'اضغط مسح لجلب إشارات' : 'Click Scan to fetch signals')}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketAnalysisPage;
